import React, { useState, useEffect } from "react";
import { Typography, Grid, Card, Avatar, Box, Button, Alert } from "@mui/material";
import { useParams } from "react-router-dom";
import { useNavigate,useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { colors } from "../components/Theme/WebTheme";
import moment from "moment";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { makeStyles } from "@mui/styles";
import { FONT_FAMILY, formatCurrency, labelText, isGeneralCargoVehicleType, getCargoDimensions, hasAcceptedDriver, normalizeDomesticBookingInfo, shouldShowDomesticBookingInfo, normalizeImportBookingInfo, shouldShowImportBookingInfo, getImportDetentionStatus, openEmptyContainerReturnProofPrint, normalizeExportBookingInfo, shouldShowExportBookingInfo, isPickupDeliveryService, normalizePickupDeliveryInfo, getContainerSizeFromCarType, getBookingStatusLabel, getBookingStatusColor, getAcceptedOfferSummary, getFinalBidAmount, getCommissionTypeDisplay, getCommissionRateDisplay, getCommissionAmountDisplay, getEstimatedPayeeDisplay, getEstimatedEarningsDisplay } from "../common/sharedFunctions"
import GoBackButton from "components/GoBackButton";
import { api } from "common";
import { getBookingWorkflowForBookingRecord } from "common/src/other/bookingWorkflow";
import { EMPTY_CONTAINER_RETURN_PHOTO_REQUIREMENTS, getEmptyContainerReturnDisplayPhotos } from "common/src/other/emptyContainerReturnProofs";
import AdminStatusBadge from "../components/AdminStatus";
import { PageHeader, StatusBadge } from "../components/adminPrimitives";
import adminTokens from "../styles/adminDesignTokens";

const logisticsDisplayFields = [
  "containerNumber",
  "additionalContainerNumbers",
  "bookingNumber",
  "blNumber",
  "shippingLine",
  "containerType",
  "containerSize",
  "shipmentType",
  "pickupPort",
  "deliveryWarehouse",
  "pickupContainerYardName",
  "emptyReturnContainerYardName",
  "yardGateStatus",
  "yardGateRejectionReason",
  "freeTimeExpiry",
  "detentionDeadline",
  "demurrageDeadline",
  "dateConfirmationOk"
];

const formatProofValue = (value) => {
  if (value === true) return "true";
  if (value === false) return "false";
  if (Array.isArray(value)) return value.filter(Boolean).join(", ");
  return value;
};

const getProofRows = (t, data, isAdmin) => {
  const rows = [];
  const tr = (key, fallback) => labelText(t, key, fallback);
  const addProofRows = (prefix, proof, options = {}) => {
    if (!proof) return;
    const showRestrictedProofDetails = isAdmin || !options.restrictedProofDetails;
    rows.push({ key: `${prefix}_timestamp`, label: `${tr(prefix)} ${tr("time_date_stamp")}`, value: proof.timestampIso });
    rows.push({ key: `${prefix}_lat`, label: `${tr(prefix)} ${tr("latitude")}`, value: proof.lat });
    rows.push({ key: `${prefix}_lng`, label: `${tr(prefix)} ${tr("longitude")}`, value: proof.lng });
    if (showRestrictedProofDetails) {
      rows.push({ key: `${prefix}_geofence`, label: `${tr(prefix)} ${tr("terminal_geofence_proof")}`, value: formatProofValue(proof.terminalGeofenceProof) });
      rows.push({ key: `${prefix}_distance`, label: `${tr(prefix)} ${tr("terminal_distance_km")}`, value: proof.terminalDistanceKm });
    }
    rows.push({ key: `${prefix}_stamp`, label: `${tr(prefix)} ${tr("photo_stamp", "Photo Stamp")}`, value: proof.stampText });
    if (showRestrictedProofDetails) {
      rows.push({ key: `${prefix}_hash`, label: `${tr(prefix)} ${tr("proof_hash")}`, value: proof.proofHash });
      rows.push({ key: `${prefix}_prev_hash`, label: `${tr(prefix)} ${tr("previous_proof_hash")}`, value: proof.previousProofHash });
    }
  };

  addProofRows("gps_arrival_proof", data?.arrivalProof);
  addProofRows("pickup_photo_proof", data?.pickup_image_proof, { restrictedProofDetails: true });
  addProofRows("delivery_photo_proof", data?.deliver_image_proof, { restrictedProofDetails: true });
  addProofRows("container_yard_photo_proof", data?.container_yard_image_proof);
  addProofRows("container_yard_video_proof", data?.container_yard_video_proof);
  if (data?.driverSwornStatement) {
    rows.push({ key: "driver_sworn_statement", label: tr("driver_sworn_statement"), value: data.driverSwornStatement.statement });
    rows.push({ key: "driver_sworn_signature", label: tr("type_driver_signature"), value: data.driverSwornStatement.signature });
    rows.push({ key: "driver_sworn_signed_at", label: tr("certificate_signed_at"), value: data.driverSwornStatement.signedAtIso });
    rows.push({ key: "driver_sworn_location", label: tr("location"), value: `${data.driverSwornStatement.lat || ""}, ${data.driverSwornStatement.lng || ""}` });
    rows.push({ key: "driver_sworn_hash", label: tr("driver_sworn_statement_hash"), value: data.driverSwornStatementHash });
  }
  if (data?.arrivalTimestampCertificate) {
    rows.push({ key: "arrival_certificate_sent", label: tr("arrival_timestamp_certificate"), value: data.arrivalTimestampCertificate.emailSent ? tr("email_sent") : tr("email_not_sent") });
    rows.push({ key: "arrival_certificate_sent_at", label: tr("certificate_sent_at"), value: data.arrivalTimestampCertificate.sentAt ? new Date(data.arrivalTimestampCertificate.sentAt).toLocaleString() : "" });
    rows.push({ key: "arrival_certificate_recipients", label: tr("certificate_recipients"), value: data.arrivalTimestampCertificate.recipients ? data.arrivalTimestampCertificate.recipients.join(", ") : "" });
  }
  if (data?.offlineDriverNotes) {
    data.offlineDriverNotes.forEach((note, index) => {
      rows.push({ key: `offline_note_${index}`, label: `${tr("offline_driver_note")} ${index + 1}`, value: note.note });
    });
  }
  return rows.filter((item) => item.value !== null && item.value !== undefined && item.value !== "");
};

const getEmptyReturnConsigneeName = (booking) => {
  if (!booking) {
    return "";
  }
  return booking.importBookingInfo && booking.importBookingInfo.consigneeName
    ? booking.importBookingInfo.consigneeName
    : booking.consigneeName || booking.customer_name || "";
};

const canShowEmptyReturnWorkflow = (booking, workflowConfigs) => {
  return !!getBookingWorkflowForBookingRecord(booking, workflowConfigs).allowEmptyReturn;
};

const useStyles = makeStyles((theme) => ({
  card: {
    borderRadius: "22px",
    backgroundColor: "rgba(255,255,255,0.96)",
    minHeight: 100,
    marginTop: 5,
    marginBottom: 20,
    boxShadow: "0 18px 48px rgba(3, 29, 54, 0.10)",
    border: "1px solid rgba(8, 34, 61, 0.10)",
    overflow: "hidden",
  },
  txt: {
    padding: "16px 20px",
    fontWeight: 900,
    minHeight: 58,
    background: `linear-gradient(135deg, ${adminTokens.colors.deepNavy}, ${adminTokens.colors.royalBlue})`,
    color: colors.WHITE,
    boxShadow: "none",
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
  },
}));

function BookingDetails() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const label = (key, fallback) => labelText(t, key, fallback);
  const isRTL = i18n.dir();
  const bookinglistdata = useSelector((state) => state.bookinglistdata);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [emptyReturnOpen, setEmptyReturnOpen] = useState(false);
  const workflowconfigs = useSelector((state) => state.workflowconfigs) || { configs: [] };
  const { fetchWorkflowConfigurations } = api;
  const settings = useSelector((state) => state.settingsdata.settings);
  const classes = useStyles();
  const authProfile = useSelector(state => state.auth.profile);
  const role = authProfile.usertype;
  const { state } = useLocation();

  useEffect(() => {
    if (fetchWorkflowConfigurations) {
      dispatch(fetchWorkflowConfigurations());
    }
  }, [dispatch, fetchWorkflowConfigurations]);
  
  useEffect(() => {
    if (bookinglistdata.bookings) {
      const booking = bookinglistdata.bookings.filter(
        (item) => item.id === id.toString()
      )[0];

      if (booking) {
        setData(booking);
      } else {
        navigate("/404");
        setData([]);
      }
    }
  }, [bookinglistdata.bookings, id, navigate]);

  const renderGridItem = (item, isRTL) => {
    return (
      <Grid
        key={item.key}
        container
        sx={{ direction: isRTL === "rtl" ? "rtl" : "ltr" }}
        style={{
          alignItems: "stretch",
          minHeight: 48,
          borderLeft: `1px solid ${adminTokens.colors.border}`,
          borderRight: `1px solid ${adminTokens.colors.border}`,
          borderBottom: `1px solid ${adminTokens.colors.border}`,
          ...item.addressGridStyle
        }}
      >
        <Grid item xs={5} style={{ backgroundColor: "#F7FBFF", display: "flex", alignItems: "center" }}>
          <Typography
            style={{
              fontSize: 14,
              padding: 8,
              textAlign: isRTL === "rtl" ? "right" : "left",
              fontFamily:FONT_FAMILY,
              fontWeight: "bold",
              color: adminTokens.colors.deepNavy,
              ...item.typographyStyleAddress
            }}
          >
            {item.label}
          </Typography>
        </Grid>
        <Grid item xs={7} style={{ display: "flex", alignItems: "center", justifyContent: isRTL === "rtl" ? "flex-start" : "flex-end", backgroundColor: colors.WHITE }}>
          {item.type === "image" && item.value ?
            <img
              src={item.value}
              alt={item.alt}
              style={{
                width: 120,
                height: 90,
                objectFit: "cover",
                margin: 8,
              }}
            />
          :
            <Typography
              style={{
                fontSize: 14,
                padding: 8,
                textAlign: isRTL === "rtl" ? "left" : "right",
                wordBreak: 'break-word',
                fontFamily:FONT_FAMILY,
                color: adminTokens.colors.text,
                ...item.style,
              }}
            >
              {item.value}
            </Typography>
          }
        </Grid>
      </Grid>
    );
  };

  const renderTypography = (text) => {
    return (
      <Typography
      className={classes.txt}
      style={{
        fontFamily:FONT_FAMILY,
      }}
      variant="h5"
      >
        {text}
      </Typography>
    );
  };

  const formatDetailValue = (value) => {
    if (Array.isArray(value)) return value.filter(Boolean).join(", ");
    if (value === true) return label("true", "True");
    if (value === false) return label("false", "False");
    return value;
  };

  const renderDetailImage = (item) => (
    <Box style={{ position: "relative", display: "inline-block", width: 180, maxWidth: "100%" }}>
      <img src={item.value} alt={item.alt || item.key} style={{ width: "100%", height: 120, objectFit: "cover", display: "block" }} />
      {item.stampText && !item.stampEmbedded ? (
        <Box
          style={{
            position: "absolute",
            left: 8,
            bottom: 8,
            color: "#fff",
            fontWeight: "bold",
            fontSize: 11,
            lineHeight: 1.25,
            whiteSpace: "pre-line",
            textShadow: "1px 1px 3px #000",
            background: "transparent"
          }}
        >
          {item.stampText}
        </Box>
      ) : null}
    </Box>
  );

  const renderWideDetailsTable = (title, rows) => {
    const visibleRows = (rows || []).map((item) => ({ ...item, value: formatDetailValue(item.value) })).filter((item) => item.value !== null && item.value !== undefined && item.value !== "");
    if (!visibleRows.length) return null;
    const table = (
      <Box style={{ padding: 10, overflowX: "auto" }}>
        <Box component="table" style={{ width: "100%", minWidth: 720, borderCollapse: "collapse", fontFamily: FONT_FAMILY }}>
          <Box component="thead">
            <Box component="tr">
              {visibleRows.map((item) => (
                <Box
                  component="th"
                  key={`${item.key}-head`}
                  style={{
                    backgroundColor: "#F7FBFF",
                    border: `1px solid ${adminTokens.colors.border}`,
                    color: adminTokens.colors.deepNavy,
                    padding: 8,
                    fontSize: 13,
                    textAlign: "left",
                    whiteSpace: "normal"
                  }}
                >
                  {item.label}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            <Box component="tr">
              {visibleRows.map((item) => (
                <Box
                  component="td"
                  key={`${item.key}-value`}
                  style={{
                    border: `1px solid ${adminTokens.colors.border}`,
                    color: adminTokens.colors.text,
                    padding: 8,
                    fontSize: 13,
                    verticalAlign: "top",
                    wordBreak: "break-word",
                    maxWidth: 280
                  }}
                >
                  {item.type === "image" && item.value ?
                    renderDetailImage(item)
                  :
                    item.value
                  }
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    );
    return (
      <Grid item xs={12}>
        <Card className={classes.card}>
          {renderTypography(title)}
          {table}
        </Card>
      </Grid>
    );
  };

  const renderInlineWideDetailsTable = (rows) => {
    const visibleRows = (rows || []).map((item) => ({ ...item, value: formatDetailValue(item.value) })).filter((item) => item.value !== null && item.value !== undefined && item.value !== "");
    if (!visibleRows.length) return null;
    return (
      <Box style={{ padding: 10, overflowX: "auto" }}>
        <Box component="table" style={{ width: "100%", minWidth: 720, borderCollapse: "collapse", fontFamily: FONT_FAMILY }}>
          <Box component="thead">
            <Box component="tr">
              {visibleRows.map((item) => (
                <Box component="th" key={`${item.key}-head`} style={{ backgroundColor: "#F7FBFF", border: `1px solid ${adminTokens.colors.border}`, color: adminTokens.colors.deepNavy, padding: 8, fontSize: 13, textAlign: "left" }}>
                  {item.label}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            <Box component="tr">
              {visibleRows.map((item) => (
                <Box component="td" key={`${item.key}-value`} style={{ border: `1px solid ${adminTokens.colors.border}`, color: adminTokens.colors.text, padding: 8, fontSize: 13, verticalAlign: "top", wordBreak: "break-word", maxWidth: 280 }}>
                  {item.type === "image" && item.value ? renderDetailImage(item) : item.value}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  const detailContainerSize = data ? (getContainerSizeFromCarType(data.carType || "") || data.containerSize || "") : "";
  const showContainerShippingDetails = detailContainerSize === "20ft" || detailContainerSize === "40ft";
  const logisticsDetails = data && showContainerShippingDetails && logisticsDisplayFields
    .map((field) => ({ key: field, label: labelText(t, field), value: field === "dateConfirmationOk" && data?.[field] ? "OK" : data?.[field] }))
    .filter((item) => item.value);
  const isPickupDeliveryBooking = isPickupDeliveryService(data || {});
  const cargoDetails = data ? [
  { key: "itemDescription", label: labelText(t, "itemDescription"), value: data.itemDescription },
  { key: "grossWeight", label: labelText(t, "grossWeight"), value: data.grossWeight },
  { key: "cargoDimensions", label: labelText(t, "cargoDimensions"), value: getCargoDimensions(data) },
  { key: "numberOfPieces", label: labelText(t, "numberOfPieces"), value: data.numberOfPieces },
  { key: "note", label: labelText(t, "note"), value: data.note },
  { key: "exactDeliveryAddress", label: labelText(t, "exactDeliveryAddress"), value: !isPickupDeliveryBooking && hasAcceptedDriver(data) ? data.exactDeliveryAddress : "" }
  ].filter((item) => item.value) : [];
  const pickupDeliveryInfo = normalizePickupDeliveryInfo(data || {});
  const pickupDeliveryDetails = data && isPickupDeliveryBooking ? [
    { key: "serviceType", label: labelText(t, "serviceType"), value: labelText(t, data.serviceType || "pickup_delivery") },
    { key: "pickupAddress", label: labelText(t, "pickupAddress", "Pickup Address"), value: pickupDeliveryInfo.pickupAddress },
    { key: "deliveryAddress", label: labelText(t, "deliveryWarehouse", "Delivery Address"), value: pickupDeliveryInfo.deliveryAddress },
    { key: "receiverName", label: labelText(t, "receiverName"), value: pickupDeliveryInfo.receiverName },
    { key: "receiverContactNumber", label: labelText(t, "receiverContactNumber"), value: pickupDeliveryInfo.receiverContactNumber },
    { key: "preferredPickupDate", label: labelText(t, "preferredPickupDate"), value: pickupDeliveryInfo.preferredPickupDate },
    { key: "preferredPickupTime", label: labelText(t, "preferredPickupTime"), value: pickupDeliveryInfo.preferredPickupTime }
  ].filter((item) => item.value) : [];
  const waypointRows = data && isPickupDeliveryBooking && Array.isArray(data.waypoints) && data.waypoints.length > 0 ? [
    { key: "pickupLocation", label: labelText(t, "pickup_location", "Pickup Location"), value: data.pickupAddress || (data.pickup && data.pickup.add) },
    ...data.waypoints.map((point, index) => ({
      key: `stop${index + 1}`,
      label: `${labelText(t, "stop", "Stop")} ${index + 1}`,
      value: point && (point.add || point.description)
    })),
    { key: "finalDeliveryLocation", label: labelText(t, "final_delivery_location", "Final Delivery Location"), value: data.dropAddress || (data.drop && data.drop.add) }
  ].filter((item) => item.value) : [];
  const domesticInfo = normalizeDomesticBookingInfo(data || {});
  const domesticDetails = data && !isPickupDeliveryBooking && shouldShowDomesticBookingInfo(data, data.carType) ? [
    { key: "domesticBookingNumber", label: labelText(t, "domesticBookingNumber"), value: domesticInfo.bookingNumber },
    { key: "domesticBookingDate", label: labelText(t, "domesticBookingDate"), value: domesticInfo.bookingDate },
    { key: "domesticShipmentType", label: labelText(t, "domesticShipmentType"), value: domesticInfo.shipmentType },
    { key: "shipperName", label: labelText(t, "shipperName"), value: domesticInfo.shipperName },
    { key: "consigneeName", label: labelText(t, "consigneeName"), value: domesticInfo.consigneeName },
    { key: "contactPerson", label: labelText(t, "contactPerson"), value: domesticInfo.contactPerson },
    { key: "contactNumber", label: labelText(t, "contactNumber"), value: domesticInfo.contactNumber },
    { key: "completeDeliveryAddress", label: labelText(t, "completeDeliveryAddress"), value: domesticInfo.completeDeliveryAddress }
  ].filter((item) => item.value) : [];
  const importInfo = normalizeImportBookingInfo(data || {}, data && data.carType);
  const showImportBookingDetails = data && !isPickupDeliveryBooking && shouldShowImportBookingInfo(data, data.carType);
  const importDetails = showImportBookingDetails ? [
    { key: "importConsigneeName", label: labelText(t, "consigneeName"), value: importInfo.consigneeName },
    { key: "importContainerNumber", label: labelText(t, "containerNumber"), value: importInfo.containerNumber },
    { key: "importBlNumber", label: labelText(t, "blAwbNumber", "BL/AWB Number"), value: importInfo.blNumber },
    { key: "importBookingNumber", label: labelText(t, "bookingNumber"), value: importInfo.bookingNumber },
    { key: "importShippingLine", label: labelText(t, "shippingLine"), value: importInfo.shippingLine },
    { key: "importPickupPort", label: labelText(t, "pickupPort"), value: importInfo.pickupPort },
    { key: "importDeliveryAddress", label: labelText(t, "deliveryWarehouse"), value: importInfo.deliveryAddress },
    { key: "importContainerType", label: labelText(t, "containerType"), value: importInfo.containerType },
    { key: "importContainerSize", label: labelText(t, "containerSize"), value: importInfo.containerSize },
    { key: "importFreeTimeExpiry", label: labelText(t, "freeTimeExpiry"), value: importInfo.freeTimeExpiry },
    { key: "importDetentionDeadline", label: labelText(t, "detentionDeadline"), value: importInfo.detentionDeadline },
    { key: "importDemurrageDeadline", label: labelText(t, "demurrageDeadline"), value: importInfo.demurrageDeadline }
  ].filter((item) => item.value) : [];
  const detentionInfo = showImportBookingDetails ? getImportDetentionStatus({ booking: data, importBookingInfo: importInfo }) : null;
  const detentionEmptyValue = labelText(t, "not_available", "N/A");
  const formatDetentionDate = (value) => {
    if (!value) return "";
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format("lll") : value;
  };
  const formatDetentionMetric = (value) => (value === 0 || value ? value : "");
  const detentionAlertSeverity = detentionInfo && detentionInfo.isOverdue
    ? "error"
    : (detentionInfo && detentionInfo.isDueToday ? "warning" : (detentionInfo && detentionInfo.detentionStatus === "Safe" ? "success" : "info"));
  const detentionAlertRows = detentionInfo ? [
    { key: "lastFreeDay", label: labelText(t, "lastFreeDay", "Last Free Day"), value: formatDetentionDate(detentionInfo.lastFreeDay) },
    { key: "detentionStartDate", label: labelText(t, "detentionStartDate", "Detention Start Date"), value: formatDetentionDate(detentionInfo.detentionStartDate) },
    { key: "daysRemaining", label: labelText(t, "days_remaining", "Days Remaining"), value: formatDetentionMetric(detentionInfo.daysRemaining) },
    { key: "overdueDays", label: labelText(t, "overdue_days", "Overdue Days"), value: formatDetentionMetric(detentionInfo.overdueDays) },
    { key: "detentionStatus", label: labelText(t, "detention_status", "Detention Status"), value: detentionInfo.detentionStatus },
    { key: "detentionPenalty", label: labelText(t, "detentionPenalty", "Estimated Detention Penalty"), value: detentionInfo.detentionPenalty },
    { key: "detentionRemarks", label: labelText(t, "detentionRemarks", "Detention Remarks"), value: detentionInfo.detentionRemarks }
  ] : [];
  const exportInfo = normalizeExportBookingInfo(data || {}, data && data.carType);
  const exportDetails = data && !isPickupDeliveryBooking && shouldShowExportBookingInfo(data, data.carType) ? [
    { key: "exportExporterShipperName", label: labelText(t, "exporterShipperName"), value: exportInfo.exporterShipperName },
    { key: "exportPortOfLoading", label: labelText(t, "portOfLoading"), value: exportInfo.portOfLoading },
    { key: "exportContainerNumber", label: labelText(t, "containerNumber"), value: exportInfo.containerNumber },
    { key: "exportBlNumber", label: labelText(t, "blAwbNumber", "BL/AWB Number"), value: exportInfo.blNumber },
    { key: "exportBookingNumber", label: labelText(t, "bookingNumber"), value: exportInfo.bookingNumber },
    { key: "exportShippingLine", label: labelText(t, "shippingLine"), value: exportInfo.shippingLine },
    { key: "exportDeliveryAddress", label: labelText(t, "deliveryWarehouse"), value: exportInfo.deliveryAddress },
    { key: "exportContainerType", label: labelText(t, "containerType"), value: exportInfo.containerType },
    { key: "exportContainerSize", label: labelText(t, "containerSize"), value: exportInfo.containerSize },
    { key: "exportVesselEtd", label: labelText(t, "vesselEtd"), value: exportInfo.vesselEtd },
    { key: "exportCargoCutOffDate", label: labelText(t, "cargoCutOffDate"), value: exportInfo.cargoCutOffDate }
  ].filter((item) => item.value) : [];

  const isAdmin = role === 'admin';
  const proofDetails = data && getProofRows(t, data, isAdmin);
  const emptyContainerReturns = data && data.emptyContainerReturns ? Object.values(data.emptyContainerReturns).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)) : [];
  const emptyPickUpContainer = data && data.emptyPickUpContainer ? data.emptyPickUpContainer : null;
  const emptyPickUpContainerDetails = emptyPickUpContainer ? [
    { key: "empty_pickup_yard", label: labelText(t, "empty_pickup_container_yard", "Empty Pick Up Container Yard"), value: emptyPickUpContainer.emptyPickupContainerYardName },
    { key: "empty_pickup_datetime", label: labelText(t, "empty_pickup_datetime", "Pickup Date & Time"), value: emptyPickUpContainer.pickupDateTime ? moment(emptyPickUpContainer.pickupDateTime).format("lll") : "" },
    { key: "empty_pickup_eir", label: labelText(t, "eir_number", "EIR Number"), value: emptyPickUpContainer.eirNumber },
    { key: "empty_pickup_condition", label: labelText(t, "empty_container_condition", "Empty Container Condition"), value: emptyPickUpContainer.emptyContainerCondition },
    { key: "empty_pickup_pullout", label: labelText(t, "last_empty_pullout_date", "Last Empty Pullout Date"), value: emptyPickUpContainer.lastEmptyPulloutDate ? moment(emptyPickUpContainer.lastEmptyPulloutDate).format("lll") : "" },
    {
      key: "empty_pickup_picture",
      label: labelText(t, "gps_picture_with_timestamp", "GPS Picture with Timestamp"),
      value: emptyPickUpContainer.gpsPictureUrl,
      type: "image",
      alt: "empty_pickup_container",
      stampText: emptyPickUpContainer.gpsPictureProof && emptyPickUpContainer.gpsPictureProof.stampText,
      stampEmbedded: emptyPickUpContainer.gpsPictureProof && emptyPickUpContainer.gpsPictureProof.stampEmbedded
    },
    { key: "empty_pickup_submitted_by", label: labelText(t, "submitted_by", "Submitted By"), value: emptyPickUpContainer.submittedBy },
    { key: "empty_pickup_submitted_at", label: labelText(t, "submitted_at", "Submitted At"), value: emptyPickUpContainer.submittedAt ? moment(emptyPickUpContainer.submittedAt).format("lll") : "" }
  ].filter((item) => item.value) : [];
  const canUseEmptyReturn = canShowEmptyReturnWorkflow(data, workflowconfigs.configs || []);
  const emptyReturnLocked = data && data.emptyContainerReturnClosed;
  const money = (value) => formatCurrency(value || 0, settings);
  const optionalMoney = (value) => value !== null && value !== undefined && value !== "" ? formatCurrency(value, settings) : "";
  const isPrivateClientBooking = !!(
    data && (data.bookingSource === "fleet_admin_client" || data.visibility === "private")
  );
  const canViewPrivateFinancials = isPrivateClientBooking && (
    role === "admin" ||
    (role === "fleetadmin" && (data.fleetadmin === authProfile.uid || data.createdBy === authProfile.uid))
  );
  const formatRateValue = (type, value) => {
    if (value === undefined || value === null || value === "") return "";
    return type === "percentage" ? `${value}%` : optionalMoney(value);
  };
  const privateBookingInfoRows = canViewPrivateFinancials ? [
    { key: "private_booking_source", label: labelText(t, "booking_source", "Booking Source"), value: data.bookingSource },
    { key: "private_visibility", label: labelText(t, "visibility", "Visibility"), value: data.visibility },
    { key: "private_created_by_role", label: labelText(t, "created_by_role", "Created By Role"), value: data.createdByRole },
    { key: "private_fleet_admin", label: labelText(t, "fleet_admin", "Fleet Admin"), value: data.fleetadmin }
  ] : [];
  const privateClientInfoRows = canViewPrivateFinancials ? [
    { key: "private_client_name", label: labelText(t, "client_name", "Client Name"), value: data.clientName },
    { key: "private_client_contact", label: labelText(t, "client_contact", "Client Contact"), value: data.clientContact },
    { key: "private_client_email", label: labelText(t, "client_email", "Client Email"), value: data.clientEmail }
  ] : [];
  const additionalFeeRows = canViewPrivateFinancials && Array.isArray(data.additionalFees)
    ? data.additionalFees.map((fee, index) => {
        const rateType = fee && fee.rateType ? fee.rateType : "flat";
        const rateValue = fee ? formatRateValue(rateType, fee.value) : "";
        const computedAmount = fee ? optionalMoney(fee.amount) : "";
        return {
          key: `private_additional_fee_${index}`,
          label: fee && fee.name ? fee.name : `${labelText(t, "additional_fees", "Additional Fee")} ${index + 1}`,
          value: [rateType, rateValue, computedAmount].filter(Boolean).join(" — ")
        };
      })
    : [];
  const privateFinancialRows = canViewPrivateFinancials ? [
    { key: "private_trucking_charge", label: labelText(t, "trucking_charge_to_client", "Trucking Charge to Client"), value: optionalMoney(data.truckingChargeToClient) },
    { key: "private_commission_type", label: labelText(t, "super_admin_commission_type", "Super Admin Commission Type"), value: data.superAdminCommissionType },
    { key: "private_commission_value", label: labelText(t, "super_admin_commission_value", "Super Admin Commission Value"), value: formatRateValue(data.superAdminCommissionType, data.superAdminCommissionValue) },
    { key: "private_commission_amount", label: labelText(t, "super_admin_commission_amount", "Super Admin Commission Amount"), value: optionalMoney(data.superAdminCommissionAmount) },
    ...additionalFeeRows,
    { key: "private_additional_fee_total", label: labelText(t, "total_additional_fee_amount", "Total Additional Fee Amount"), value: optionalMoney(data.totalAdditionalFeeAmount) },
    { key: "private_fleet_admin_net", label: labelText(t, "fleet_admin_net_amount", "Fleet Admin Net Amount"), value: optionalMoney(data.fleetAdminNetAmount) }
  ] : [];
  const privateWalletThresholdRows = canViewPrivateFinancials ? [
    { key: "private_threshold_type", label: labelText(t, "fleet_admin_threshold_type", "Fleet Admin Threshold Type"), value: data.fleetAdminWalletThresholdType },
    { key: "private_threshold_value", label: labelText(t, "fleet_admin_threshold_value", "Fleet Admin Threshold Value"), value: formatRateValue(data.fleetAdminWalletThresholdType, data.fleetAdminWalletThresholdValue) },
    { key: "private_threshold_minimum", label: labelText(t, "fleet_admin_minimum_flat_rate", "Fleet Admin Minimum Flat Rate"), value: optionalMoney(data.fleetAdminWalletMinimumFlatRate) },
    { key: "private_wallet_balance", label: labelText(t, "wallet_balance_at_booking", "Wallet Balance at Booking"), value: optionalMoney(data.walletBalanceAtBooking) },
    { key: "private_threshold_required", label: labelText(t, "wallet_threshold_required_amount", "Wallet Threshold Required Amount"), value: optionalMoney(data.walletThresholdRequiredAmount) },
    { key: "private_threshold_passed", label: labelText(t, "wallet_threshold_passed", "Wallet Threshold Passed"), value: data.walletThresholdPassed === true ? labelText(t, "true", "True") : data.walletThresholdPassed === false ? labelText(t, "false", "False") : "" }
  ] : [];
  const privateAssignmentRows = canViewPrivateFinancials ? [
    { key: "private_assigned_driver", label: labelText(t, "assigned_driver", "Assigned Driver"), value: data.assignedDriverName || data.assignedDriver },
    { key: "private_assigned_driver_contact", label: labelText(t, "assigned_driver_contact", "Assigned Driver Contact"), value: data.assignedDriverContact },
    { key: "private_assigned_vehicle", label: labelText(t, "assigned_vehicle", "Assigned Vehicle"), value: data.assignedVehicleName || data.assignedVehicle },
    { key: "private_assigned_vehicle_plate", label: labelText(t, "assigned_vehicle_plate", "Assigned Vehicle Plate"), value: data.assignedVehiclePlate },
    { key: "private_assigned_by", label: labelText(t, "assigned_by", "Assigned By"), value: data.assignedBy },
    { key: "private_assigned_at", label: labelText(t, "assigned_at", "Assigned At"), value: data.assignedAt ? moment(data.assignedAt).format("lll") : "" }
  ] : [];
  const finalBidAmount = getFinalBidAmount(data);
  const bidAmountDisplay = finalBidAmount !== null && finalBidAmount !== undefined && finalBidAmount !== ""
    ? money(finalBidAmount)
    : labelText(t, "waiting_for_bid_acceptance", "Waiting for bid acceptance");

  const renderEmptyReturnChecklist = () => (
    <Box sx={{ p: 2 }}>
      <Alert severity="info" sx={{ mb: 2 }}>
        Complete this return in the Garahex mobile app using its camera and GPS. Submitted photos and the EIR / Yard Receipt will appear here for review.
      </Alert>
      <Box component="ol" sx={{ pl: 3 }}>
        {EMPTY_CONTAINER_RETURN_PHOTO_REQUIREMENTS.map((item) => (
          <Box component="li" key={item.key} sx={{ py: 0.5 }}>
            {labelText(t, item.labelKey, item.label)} — {item.required ? "Required" : "Required if rejected/damaged"}
          </Box>
        ))}
      </Box>
      <Typography sx={{ mt: 1 }}>EIR / Yard Receipt — separate required attachment.</Typography>
    </Box>
  );

  const getEmptyReturnStatusDisplay = (entry) => {
    const value = entry.emptyReturnStatus || entry.status || "";
    if (!value) return "";
    return value.toString().charAt(0).toUpperCase() + value.toString().slice(1).toLowerCase();
  };

  const getEmptyReturnGpsValue = (entry, key) => {
    const gps = entry.emptyReturnArrivalGps || entry.arrivalPictureProof || {};
    if (key === "lat") return gps.lat !== undefined ? gps.lat : gps.latitude;
    return gps.lng !== undefined ? gps.lng : gps.longitude;
  };

  const getEmptyReturnFileUrl = (file) => {
    if (!file) return "";
    if (typeof file === "string") return file;
    return file.url || file.imageUrl || file.downloadURL || "";
  };

  const getEmptyReturnProofFileUrl = (entry, key) => getEmptyReturnFileUrl(entry.proofs && entry.proofs[key]);
  const getEmptyReturnEirReceiptUrl = (entry) => entry.emptyReturnEirReceiptUrl || entry.eirReceiptUrl || getEmptyReturnProofFileUrl(entry, "eirReceiptPhoto");
  const getEmptyReturnProofCards = (entry) => [
    ...getEmptyContainerReturnDisplayPhotos(entry).map((item) => ({
      key: item.key,
      label: labelText(t, item.labelKey, item.label),
      url: item.url,
      required: item.required,
      stampText: item.proof ? item.proof.stampText : "",
      stampEmbedded: item.proof ? item.proof.stampEmbedded : false
    })),
    {
      key: "eir_yard_receipt",
      label: labelText(t, "eir_yard_receipt", "EIR / Yard Receipt"),
      url: getEmptyReturnEirReceiptUrl(entry),
      required: true,
      stampText: "",
      stampEmbedded: false
    }
  ];

  const renderEmptyReturnProofGallery = (entry) => {
    const cards = getEmptyReturnProofCards(entry);
    if (!cards.length) return null;
    return (
      <Grid container spacing={1.5} sx={{ p: 1.25 }}>
        {cards.map((item) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={item.key}>
            <Box
              sx={{
                height: "100%",
                border: `1px solid ${adminTokens.colors.border}`,
                borderRadius: "18px",
                overflow: "hidden",
                background: "linear-gradient(180deg, #FFFFFF, #F7FBFF)",
                boxShadow: "0 12px 30px rgba(3, 29, 54, 0.08)"
              }}
            >
              {item.url ? (
                <Box sx={{ position: "relative", height: 150, background: "#EAF3FF" }}>
                  <img
                    src={item.url}
                    alt={item.label}
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  />
                  {item.stampText && !item.stampEmbedded ? (
                    <Box
                      sx={{
                        position: "absolute",
                        left: 10,
                        right: 10,
                        bottom: 10,
                        color: "#fff",
                        fontWeight: 800,
                        fontSize: 11,
                        lineHeight: 1.25,
                        whiteSpace: "pre-line",
                        textShadow: "1px 1px 3px #000"
                      }}
                    >
                      {item.stampText}
                    </Box>
                  ) : null}
                </Box>
              ) : (
                <Box
                  sx={{
                    height: 150,
                    display: "grid",
                    placeItems: "center",
                    color: item.required ? adminTokens.colors.danger : adminTokens.colors.muted,
                    background: "#F7FBFF",
                    fontWeight: 850,
                    textAlign: "center",
                    px: 2
                  }}
                >
                  {item.required ? labelText(t, "required_photo_missing", "Required photo missing") : labelText(t, "photo_not_supplied", "Photo not supplied")}
                </Box>
              )}
              <Box sx={{ p: 1.4 }}>
                <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 900, color: adminTokens.colors.text, fontSize: 13.5 }}>
                  {item.label}
                </Typography>
                <Box sx={{ mt: 0.8 }}>
                  <StatusBadge tone={item.required ? "warning" : "info"}>
                    {item.required ? labelText(t, "required", "Required") : labelText(t, "conditional", "Conditional")}
                  </StatusBadge>
                </Box>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    );
  };

  const formatMeters = (value) => {
    if (value === undefined || value === null || value === "") return "";
    const parsed = parseFloat(value);
    return isNaN(parsed) ? value : `${Math.round(parsed)} m`;
  };

  const yesNoNa = (value) => {
    if (value === true) return label("true", "True");
    if (value === false) return label("false", "False");
    return "";
  };

  const printEmptyReturnProof = (entry) => {
    const entryGps = entry.emptyReturnArrivalGps || entry.arrivalPictureProof || {};
    const detention = getImportDetentionStatus({ booking: data, importBookingInfo: importInfo, returnRecord: entry });
    const formatPrintDate = (value) => value ? moment(value).format("lll") : "";
    const printMetric = (value) => value === 0 || value ? value : "";
    const additionalContainers = Array.isArray(entry.additionalContainerNumbers)
      ? entry.additionalContainerNumbers.filter(Boolean).join(", ")
      : (Array.isArray(data.additionalContainerNumbers) ? data.additionalContainerNumbers.filter(Boolean).join(", ") : "");
    openEmptyContainerReturnProofPrint({
      title: labelText(t, "garahex_empty_container_return_proof", "Garahex Empty Container Return Proof"),
      emptyValue: labelText(t, "not_available", "N/A"),
      generatedLabel: labelText(t, "generated_date_time", "Generated Date/Time"),
      attachmentsTitle: labelText(t, "proof_attachments", "Proof Attachments"),
      footer: labelText(t, "proof_summary_footer", "This proof summary was generated from Garahex system records."),
      sections: [
        {
          title: labelText(t, "booking_information", "Booking Information"),
          rows: [
            { label: labelText(t, "booking_ref", "Booking Reference"), value: entry.bookingReference || data.reference },
            { label: labelText(t, "booking_id", "Booking ID"), value: entry.bookingId || data.id }
          ]
        },
        {
          title: labelText(t, "booking_parties", "Booking / Parties"),
          rows: [
            { label: labelText(t, "customer_name", "Customer Name"), value: entry.customerName || data.customer_name },
            { label: labelText(t, "consigneeName", "Consignee Name"), value: entry.consigneeName || getEmptyReturnConsigneeName(data) },
            { label: labelText(t, "fleetadmin_name", "Fleet Admin / Logistics Company Name"), value: entry.fleetAdminName || data.fleetadminName || data.fleetadmin },
            { label: labelText(t, "driver_name", "Driver Name"), value: entry.driverName || data.driver_name }
          ]
        },
        {
          title: labelText(t, "container_details", "Container Details"),
          rows: [
            { label: labelText(t, "containerNumber", "Container Number"), value: entry.containerNumber || data.containerNumber },
            { label: labelText(t, "additional_container_numbers", "Additional Container Numbers"), value: additionalContainers },
            { label: labelText(t, "shippingLine", "Shipping Line"), value: entry.shippingLine || importInfo.shippingLine || data.shippingLine },
            { label: labelText(t, "container_yard_name", "Container Yard Name"), value: entry.containerYardName },
            { label: labelText(t, "container_yard_address", "Container Yard Address"), value: entry.containerYardAddress || entry.containerYardLocation || data.emptyReturnContainerYardAddress || data.emptyReturnContainerYardLocation }
          ]
        },
        {
          title: labelText(t, "return_status", "Return Status"),
          rows: [
            { label: labelText(t, "container_yard_status", "Status"), value: getEmptyReturnStatusDisplay(entry) },
            { label: labelText(t, "container_yard_rejection_reason", "Rejection Reason"), value: entry.emptyReturnRejectedReason || entry.rejectionReason },
            { label: labelText(t, "container_yard_remarks", "Remarks"), value: entry.emptyReturnRemarks || entry.containerYardRemarks },
            { label: labelText(t, "yard_receiver_name", "Yard Receiver Name"), value: entry.yardReceiverName },
            { label: labelText(t, "yard_receiver_position", "Yard Receiver Position"), value: entry.yardReceiverPosition },
            { label: labelText(t, "container_yard_arrival_datetime", "Arrival Time"), value: formatPrintDate(entry.emptyReturnArrivedAt || entry.arrivalDateTime) },
            { label: labelText(t, "empty_return_completed_at", "Completion Time"), value: formatPrintDate(entry.emptyReturnCompletedAt || entry.acceptanceDate) }
          ]
        },
        {
          title: labelText(t, "gps_geofence_proof", "GPS / Geofence Proof"),
          rows: [
            { label: labelText(t, "driver_latitude", "Driver Latitude"), value: getEmptyReturnGpsValue(entry, "lat") },
            { label: labelText(t, "driver_longitude", "Driver Longitude"), value: getEmptyReturnGpsValue(entry, "lng") },
            { label: labelText(t, "gps_accuracy", "GPS Accuracy"), value: formatMeters(entryGps.accuracy) },
            { label: labelText(t, "gps_captured_at", "GPS Captured At"), value: formatPrintDate(entryGps.capturedAt) },
            { label: labelText(t, "geofence_checked", "Geofence Checked"), value: entry.emptyReturnGeofenceChecked === true ? label("true", "True") : "" },
            { label: labelText(t, "geofence_passed", "Geofence Passed"), value: entry.emptyReturnGeofenceChecked === true ? yesNoNa(entry.emptyReturnGeofencePassed) : "" },
            { label: labelText(t, "geofence_distance_meters", "Distance From Yard"), value: formatMeters(entry.emptyReturnGeofenceDistanceMeters) },
            { label: labelText(t, "geofence_radius_meters", "Geofence Radius"), value: formatMeters(entry.emptyReturnGeofenceRadiusMeters) },
            { label: labelText(t, "yard_latitude", "Yard Latitude"), value: entry.emptyReturnYardLatitude },
            { label: labelText(t, "yard_longitude", "Yard Longitude"), value: entry.emptyReturnYardLongitude }
          ]
        },
        {
          title: labelText(t, "detention_free_time", "Detention / Free Time"),
          rows: [
            { label: labelText(t, "lastFreeDay", "Last Free Day"), value: formatPrintDate(detention.lastFreeDay) },
            { label: labelText(t, "detentionStartDate", "Detention Start Date"), value: formatPrintDate(detention.detentionStartDate) },
            { label: labelText(t, "detention_status", "Detention Status"), value: detention.detentionStatus },
            { label: labelText(t, "days_remaining", "Days Remaining"), value: printMetric(detention.daysRemaining) },
            { label: labelText(t, "overdue_days", "Overdue Days"), value: printMetric(detention.overdueDays) },
            { label: labelText(t, "detentionPenalty", "Estimated Penalty"), value: detention.detentionPenalty },
            { label: labelText(t, "detentionRemarks", "Detention Remarks"), value: detention.detentionRemarks }
          ]
        },
        {
          title: labelText(t, "notification", "Notification"),
          rows: [
            { label: labelText(t, "email_sent", "Email Sent"), value: entry.notification && entry.notification.emailSent ? label("true", "True") : "" },
            { label: labelText(t, "email_sent_at", "Email Sent At"), value: entry.notification && entry.notification.sentAt ? formatPrintDate(entry.notification.sentAt) : "" }
          ]
        }
      ],
      attachments: [
        ...getEmptyContainerReturnDisplayPhotos(entry).map((item) => ({
          label: labelText(t, item.labelKey, item.label),
          url: item.url
        })),
        { label: labelText(t, "eir_yard_receipt", "EIR / Yard Receipt"), url: getEmptyReturnEirReceiptUrl(entry) }
      ]
    });
  };

  const renderEmptyReturnRows = (entry, index) => [
    { key: `empty_return_booking_${index}`, label: labelText(t, "booking_id"), value: entry.bookingId || data.id },
    { key: `empty_return_reference_${index}`, label: labelText(t, "booking_ref", "Booking Reference"), value: entry.bookingReference || data.reference },
    { key: `empty_return_driver_${index}`, label: labelText(t, "driver_name"), value: entry.driverName || data.driver_name },
    { key: `empty_return_customer_${index}`, label: labelText(t, "customer_name", "Customer Name"), value: entry.customerName || data.customer_name },
    { key: `empty_return_consignee_${index}`, label: labelText(t, "consigneeName", "Consignee Name"), value: entry.consigneeName || getEmptyReturnConsigneeName(data) },
    { key: `empty_return_fleet_${index}`, label: labelText(t, "fleetadmin_name", "Fleet Admin / Logistics Company Name"), value: entry.fleetAdminName || data.fleetadminName || data.fleetadmin },
    { key: `empty_return_container_${index}`, label: labelText(t, "containerNumber"), value: entry.containerNumber || data.containerNumber },
    { key: `empty_return_status_${index}`, label: labelText(t, "container_yard_status"), value: getEmptyReturnStatusDisplay(entry) },
    { key: `empty_return_yard_${index}`, label: labelText(t, "container_yard_name"), value: entry.containerYardName },
    { key: `empty_return_containers_${index}`, label: labelText(t, "additional_container_numbers"), value: entry.additionalContainerNumbers ? entry.additionalContainerNumbers.join(", ") : "" },
    { key: `empty_return_arrival_${index}`, label: labelText(t, "container_yard_arrival_datetime"), value: (entry.emptyReturnArrivedAt || entry.arrivalDateTime) ? moment(entry.emptyReturnArrivedAt || entry.arrivalDateTime).format("lll") : "" },
    { key: `empty_return_completed_${index}`, label: labelText(t, "empty_return_completed_at", "Completion Time"), value: (entry.emptyReturnCompletedAt || entry.acceptanceDate) ? moment(entry.emptyReturnCompletedAt || entry.acceptanceDate).format("lll") : "" },
    { key: `empty_return_rejected_${index}`, label: labelText(t, "container_yard_rejection_reason"), value: entry.emptyReturnRejectedReason || entry.rejectionReason },
    { key: `empty_return_remarks_${index}`, label: labelText(t, "container_yard_remarks", "Container Yard Remarks"), value: entry.emptyReturnRemarks || entry.containerYardRemarks },
    { key: `empty_return_receiver_name_${index}`, label: labelText(t, "yard_receiver_name", "Yard Receiver Name"), value: entry.yardReceiverName },
    { key: `empty_return_receiver_position_${index}`, label: labelText(t, "yard_receiver_position", "Yard Receiver Position"), value: entry.yardReceiverPosition },
    { key: `empty_return_eir_${index}`, label: labelText(t, "eir_yard_receipt", "EIR / Yard Receipt"), value: getEmptyReturnEirReceiptUrl(entry) },
    { key: `empty_return_accepted_${index}`, label: labelText(t, "container_yard_acceptance_date"), value: entry.acceptanceDate ? moment(entry.acceptanceDate).format("lll") : "" },
    ...getEmptyContainerReturnDisplayPhotos(entry).map((item) => ({
      key: `empty_return_${item.key}_${index}`,
      label: `${labelText(t, item.labelKey, item.label)} — ${item.required ? "Required" : "Conditional"}`,
      value: item.url || (item.required ? "Required photo missing" : "Photo not supplied"),
      type: item.url ? "image" : undefined,
      alt: item.key,
      stampText: item.proof ? item.proof.stampText : "",
      stampEmbedded: item.proof ? item.proof.stampEmbedded : false
    })),
    { key: `empty_return_lat_${index}`, label: labelText(t, "latitude"), value: getEmptyReturnGpsValue(entry, "lat") },
    { key: `empty_return_lng_${index}`, label: labelText(t, "longitude"), value: getEmptyReturnGpsValue(entry, "lng") },
    { key: `empty_return_gps_accuracy_${index}`, label: labelText(t, "gps_accuracy", "GPS Accuracy"), value: formatMeters((entry.emptyReturnArrivalGps || entry.arrivalPictureProof || {}).accuracy) },
    { key: `empty_return_gps_captured_${index}`, label: labelText(t, "gps_captured_at", "GPS Captured At"), value: (entry.emptyReturnArrivalGps || {}).capturedAt ? moment(entry.emptyReturnArrivalGps.capturedAt).format("lll") : "" },
    { key: `empty_return_geofence_checked_${index}`, label: labelText(t, "geofence_checked", "Geofence Checked"), value: entry.emptyReturnGeofenceChecked === true ? label("true", "True") : "" },
    { key: `empty_return_geofence_passed_${index}`, label: labelText(t, "geofence_passed", "Geofence Passed"), value: entry.emptyReturnGeofenceChecked === true ? yesNoNa(entry.emptyReturnGeofencePassed) : "" },
    { key: `empty_return_geofence_distance_${index}`, label: labelText(t, "geofence_distance_meters", "Distance from Yard"), value: formatMeters(entry.emptyReturnGeofenceDistanceMeters) },
    { key: `empty_return_geofence_radius_${index}`, label: labelText(t, "geofence_radius_meters", "Radius Used"), value: formatMeters(entry.emptyReturnGeofenceRadiusMeters) },
    { key: `empty_return_yard_lat_${index}`, label: labelText(t, "yard_latitude", "Yard Latitude"), value: entry.emptyReturnYardLatitude },
    { key: `empty_return_yard_lng_${index}`, label: labelText(t, "yard_longitude", "Yard Longitude"), value: entry.emptyReturnYardLongitude },
    { key: `empty_return_stamp_${index}`, label: labelText(t, "time_date_stamp"), value: entry.arrivalPictureProof && entry.arrivalPictureProof.timestampIso },
    { key: `empty_return_photo_stamp_${index}`, label: labelText(t, "photo_stamp", "Photo Stamp"), value: entry.arrivalPictureProof && entry.arrivalPictureProof.stampText },
    { key: `empty_return_email_${index}`, label: labelText(t, "email_sent"), value: entry.notification && entry.notification.emailSent ? label("true", "True") : "" },
  ].filter((item) => item.value);

  return (
    <>
         <GoBackButton isRTL={isRTL}  onClick={() => { navigate('/bookings', { state: { pageNo: state?.pageNo }} ) }} />
      <PageHeader
        eyebrow={label("booking_details", "Booking Details")}
        title={data?.reference || data?.bookingNumber || data?.id || id}
        description={
          data?.pickupAddress || data?.dropAddress
            ? `${data?.pickupAddress || ""}${data?.pickupAddress && data?.dropAddress ? " → " : ""}${data?.dropAddress || ""}`
            : label("booking_details_description", "Review the shipment record, assignment, payment, cargo, and empty-container-return proof data.")
        }
        actions={
          data ? (
            <>
              <AdminStatusBadge status={data.status || getBookingStatusLabel(data, role, t)} />
              <StatusBadge tone="info">{data.carType || label("booking", "Booking")}</StatusBadge>
            </>
          ) : null
        }
      />
      <Box sx={{ flexGrow: 1 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={12} lg={12}>
            <Grid item>
              <Card className={classes.card}> 
              {renderTypography(label("ride_information"))}
                <Grid container direction="column" style={{ padding: 10 }}>
                  {data &&
                    [
                      { key: "booking_id", label: label("booking_id"), value: data.id },
                      { key: "booking_ref", label: label("booking_ref"), value: data.reference },
                      {
                        key: "booking_status_web",
                        label: label("booking_status_web"),
                        value: getBookingStatusLabel(data, role, t),
                        style: {
                          backgroundColor:
                          getBookingStatusColor(data),
                          color:colors.WHITE,
                          fontWeight: "bold",
                          borderRadius: "10px",
                          textAlign: "center",
                          padding: 3,
                        },
                      },
                      { key: "accepted_offer", label: labelText(t, "accepted_offer", "Accepted Offer"), value: getAcceptedOfferSummary(data, settings, t) },
                      {
                        key: "trip_start_date", label: label("trip_start_date"), value: data?.tripdate
                          ? moment(data?.tripdate).format("lll")
                          : ""
                      },
                      { key: "trip_start_time", label: label("trip_start_time"), value: data?.trip_start_time },
                      { key: "trip_end_time", label: label("trip_end_time"), value: data?.trip_end_time },
                      {
                        key: "parcel_type_web", label: label("parcel_type_web"), value: data.parcelTypeSelected
                          ? data.parcelTypeSelected.description +
                          " (" +
                          money(data.parcelTypeSelected.amount) +
                          ")"
                          : ""
                      },
                      {
                        key: "parcel_option", label: label("parcel_option"), value: data.optionSelected
                          ? data.optionSelected.description +
                          " (" +
                          money(data.optionSelected.amount) +
                          ")"
                          : ""
                      },
                      { key: "deliveryPerson", label: label("deliveryPerson"), value: data?.deliveryPerson },
                      {
                        key: "deliveryPersonPhone", label: label("deliveryPersonPhone"), value: data.deliveryPersonPhone ? (settings.AllowCriticalEditsAdmin
                          ? data.deliveryPersonPhone
                          : label("hidden_demo")) : ""
                      },
                      { key: "take_pickup_image_web", label: label("take_pickup_image_web"), value: data.pickup_image ? data.pickup_image : "", type: 'image', alt: "pickup_image" },
                      { key: "take_deliver_image_web", label: label("take_deliver_image_web"), value: data.deliver_image ? data.deliver_image : "", type: 'image', alt: "deliver_image" },
                      { key: "take_container_yard_image", label: label("take_container_yard_image"), value: data.container_yard_image ? data.container_yard_image : "", type: 'image', alt: "container_yard_image" },
                      { key: "take_container_yard_video", label: label("take_container_yard_video"), value: data.container_yard_video ? data.container_yard_video : "" },
                      { key: "tripInstructions", label: label("tripInstructions"), value: data?.tripInstructions },
                      { key: "cancellation_reason", label: label("cancellation_reason"), value: data?.reason },
                      { key: "otp", label: label("otp"), value: data?.otp  ? label("true", "True") : label("false", "False")},
                      { key: "total_time", label: label("total_time"), value: data?.total_trip_time },
                      {
                        key: "distance_web", label: label("distance_web"), value: data.distance ? (settings.convert_to_mile
                          ? data?.distance + label("mile", "mile")
                          : data?.distance + " " + label("km", "km")) : ""
                      },
                      { key: "pickUpInstructions_web", label: label("pickUpInstructions_web"), value: data?.pickUpInstructions },
                      { key: "deliveryInstructions", label: label("deliveryInstructions"), value: data?.deliveryInstructions },
                      { key: "otherPerson", label: label("otherPerson"), value: data?.otherPerson },
                      { key: "otherPersonPhone", label: label("otherPersonPhone"), value: data?.otherPersonPhone },
                      { key: "feedback", label: label("feedback"), value: data?.feedback },
                      {
                        key: "pickup_address", label: label("pickup_address"), value: data?.pickupAddress, addressGridStyle: {
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          flexDirection: "row",
                          minHeight: 60,
                          marginBottom: 20,
                        }, typographyStyleAddress: { color:colors.GREEN }
                      },
                      { key: "drop_address", label: label("drop_address"), value: data?.dropAddress, typographyStyleAddress: { color:colors.RED, } },
                    ].map((item) =>
                      item.value ? renderGridItem(item, isRTL) : null
                    )}
                </Grid>
              </Card>
            </Grid>
            {canViewPrivateFinancials ? renderWideDetailsTable(labelText(t, "private_booking_info", "Private Booking Info"), privateBookingInfoRows) : null}
            {canViewPrivateFinancials ? renderWideDetailsTable(labelText(t, "private_client_info", "Client Info"), privateClientInfoRows) : null}
            {canViewPrivateFinancials ? renderWideDetailsTable(labelText(t, "private_financial_breakdown", "Financial Breakdown"), privateFinancialRows) : null}
            {canViewPrivateFinancials ? renderWideDetailsTable(labelText(t, "wallet_threshold_reference", "Wallet Threshold Reference"), privateWalletThresholdRows) : null}
            {canViewPrivateFinancials ? renderWideDetailsTable(labelText(t, "assignment_info", "Assignment Info"), privateAssignmentRows) : null}
            {cargoDetails && cargoDetails.length > 0 ?
              renderWideDetailsTable(labelText(t, "primary_booking_information_form", "Primary Booking Information Form"), cargoDetails)
            : null}
            {pickupDeliveryDetails && pickupDeliveryDetails.length > 0 ?
              renderWideDetailsTable(labelText(t, "pickup_delivery_information_form", "Pickup & Delivery Information"), pickupDeliveryDetails)
            : null}
            {waypointRows && waypointRows.length > 0 ?
              renderWideDetailsTable(labelText(t, "multi_drop_route", "Multi-drop Route"), waypointRows)
            : null}
            {logisticsDetails && logisticsDetails.length > 0 && showContainerShippingDetails && !isPickupDeliveryBooking && !isGeneralCargoVehicleType(data.carType) && !(importDetails && importDetails.length > 0) && !(exportDetails && exportDetails.length > 0) ?
              renderWideDetailsTable(labelText(t, "container_shipping_info"), logisticsDetails)
            : null}
            {domesticDetails && domesticDetails.length > 0 ?
              renderWideDetailsTable(labelText(t, "domestic_booking_information_form", "Domestic Booking Information Form"), domesticDetails)
            : null}
            {importDetails && importDetails.length > 0 ?
              renderWideDetailsTable(labelText(t, "import_booking_information_form", "Import Booking Information Form"), importDetails)
            : null}
            {detentionAlertRows && detentionAlertRows.length > 0 ?
              <Grid item xs={12}>
                <Alert severity={detentionAlertSeverity} style={{ marginBottom: 10 }}>
                  <Typography style={{ fontFamily: FONT_FAMILY, fontWeight: "bold", marginBottom: 8 }}>
                    {labelText(t, "detention_status", "Detention Status")}
                  </Typography>
                  <Box style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 8 }}>
                    {detentionAlertRows.map((item) => (
                      <Box key={item.key}>
                        <Typography style={{ fontFamily: FONT_FAMILY, fontSize: 12, fontWeight: "bold" }}>
                          {item.label}
                        </Typography>
                        <Typography style={{ fontFamily: FONT_FAMILY }}>
                          {item.value === 0 || item.value ? item.value : detentionEmptyValue}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Alert>
              </Grid>
            : null}
            {exportDetails && exportDetails.length > 0 ?
              renderWideDetailsTable(labelText(t, "export_booking_information_form", "Export Booking Information Form"), exportDetails)
            : null}
            {proofDetails && proofDetails.length > 0 ?
              renderWideDetailsTable(labelText(t, "gps_arrival_proof"), proofDetails)
            : null}
            {emptyPickUpContainerDetails && emptyPickUpContainerDetails.length > 0 ?
              renderWideDetailsTable(labelText(t, "empty_pickup_container_details", "Empty Pick Up Container Details"), emptyPickUpContainerDetails)
            : null}
            {canUseEmptyReturn ?
              <Grid item>
                <Card className={classes.card}>
                  {renderTypography(label("empty_container_return"))}
                  <Grid container direction="column" style={{ padding: 10 }}>
                    {emptyReturnLocked ?
                      <Alert severity="success" style={{ marginBottom: 10 }}>{label("empty_container_return_locked")}</Alert>
                    : null}
                    {role === "driver" && !emptyReturnLocked ?
                      <Button
                        disabled={emptyReturnLocked}
                        variant="contained"
                        onClick={() => setEmptyReturnOpen(!emptyReturnOpen)}
                        style={{ marginBottom: 10 }}
                      >
                        {label("empty_container_return")}
                      </Button>
                    : null}
                    {emptyReturnOpen && !emptyReturnLocked ? renderEmptyReturnChecklist() : null}
                    {emptyContainerReturns.map((entry, index) => (
                      <Grid key={entry.id || index} container direction="column" style={{ marginTop: 10 }}>
                        <Box style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", padding: 8 }}>
                          <Typography style={{ fontFamily: FONT_FAMILY, fontWeight: "bold" }}>
                            {label("empty_container_return_details")} {emptyContainerReturns.length - index}
                          </Typography>
                          <Button size="small" variant="outlined" onClick={() => printEmptyReturnProof(entry)}>
                            {labelText(t, "print_ecr_proof", "Print ECR Proof")}
                          </Button>
                        </Box>
                        {renderEmptyReturnProofGallery(entry)}
                        {renderInlineWideDetailsTable(renderEmptyReturnRows(entry, index))}
                      </Grid>
                    ))}
                  </Grid>
                </Card>
              </Grid>
            : null}
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={3}>
            <Grid item>
              <Card className={classes.card}>
                {renderTypography(label("payment_info"))}
                <Grid container direction="column" style={{ paddingRight: 15, paddingLeft: 15, paddingBottom: 15 }}>
                  {data &&
                    [
                      ...(data?.deliveryWithBid ? [
                        { key: "bid_amount", label: labelText(t, "bid_amount", "Bid Amount"), value: bidAmountDisplay },
                        { key: "total_payable", label: labelText(t, "total_payable", "Total Payable"), value: bidAmountDisplay },
                        { key: "commission_type", label: labelText(t, "commission_type", "Commission Type"), value: getCommissionTypeDisplay(data, t) },
                        { key: "commission_rate", label: labelText(t, "commission_rate", "Commission Rate"), value: getCommissionRateDisplay(data, t) },
                        { key: "commission_amount", label: labelText(t, "commission_amount", "Commission Amount"), value: getCommissionAmountDisplay(data, settings, t) },
                        ...(isAdmin || role === "fleetadmin" || role === "driver" ? [
                          { key: "estimated_payee", label: labelText(t, "estimated_payee", "Estimated Payee"), value: getEstimatedPayeeDisplay(data, t) },
                          { key: "estimated_earnings", label: labelText(t, "estimated_earnings", "Estimated Earnings"), value: getEstimatedEarningsDisplay(data, settings, t) },
                        ] : []),
                        { key: "payment_status", label: labelText(t, "payment_status", "Payment Status"), value: getBookingStatusLabel(data, role, t) },
                      ] : []),
                      {
                        key: "trip_cost", label: label("trip_cost"), value: data?.deliveryWithBid ? "" : money(data?.trip_cost)
                      },
                      {
                        key: "Customer_paid", label: label("Customer_paid"), value: optionalMoney(data?.customer_paid)
                      },
                      {
                        key: "discount_ammount",
                        label: label("discount_ammount"),
                        value: money(data?.discount),
                      },
                      {
                        key: "driver_share", label: label("driver_share"), value: money(data?.driver_share)
                      },
                      {
                        key: "fleet_admin_comission", label: label("fleet_admin_comission"), value: data?.fleetCommission && parseFloat(data?.fleetCommission) > 0 ? money(data?.fleetCommission) : ""
                      },
                      {
                        key: "convenience_fee", label: label("convenience_fee"), value: money(data?.convenience_fees)
                      },
                      ...(data?.additionalFees && Array.isArray(data.additionalFees) ? data.additionalFees.map((fee, index) => ({
                        key: `additional_fee_${index}`,
                        label: fee.name || label("additional_fees"),
                        value: money(fee.amount)
                      })) : []),
                      ...(isAdmin ? [{
                        key: "additional_fees_total", label: label("additional_fees_total"), value: optionalMoney(data?.additionalFeesTotal)
                      }] : []),
                      {
                        key: "tipamount", label: label("tipamount"), value: optionalMoney(data?.tipamount)
                      },
                      {
                        key: "cancellationFee", label: label("cancellationFee"), value: money(data?.cancellationFee)
                      },
                      {
                        key: "payment_gateway", label: label("payment_gateway"), value: data?.gateway
                      },
                      { key: "payment_mode_web", label: label("payment_mode_web"), value: data?.payment_mode ? data.payment_mode : "" },
                      {
                        key: "cash_payment_amount", label: label("cash_payment_amount"), value: optionalMoney(data?.cashPaymentAmount)
                      },
                      {
                        key: "card_payment_amount", label: label("card_payment_amount"), value:
                          optionalMoney(data?.cardPaymentAmount)
                      },
                      {
                        key: "wallet_payment_amount", label: label("wallet_payment_amount"), value:
                          optionalMoney(data?.usedWalletMoney)
                      },
                      {
                        key: "payable_ammount", label: label("payable_ammount"), value: optionalMoney(data?.payableAmount)
                      },
                    ].map((item) =>
                      item.value ? renderGridItem(item, isRTL): null
                    )}
                </Grid>
              </Card>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={3}>
            <Grid item>
              <Card className={classes.card}>
                {renderTypography(label("driver_info"))}
                <Grid container direction="column" style={{ padding: 15 }}>
                  <Grid
                    item
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {data?.driver_image ? (
                      <Avatar
                        alt="driver profile image"
                        src={data?.driver_image}
                        style={{ width: 100, height: 100, objectFit: "cover" }}
                      />
                    ) : (
                      <AccountCircleIcon sx={{ width: 100, height: 100 }} />
                    )}
                  </Grid>
                  {data &&
                    [
                      { key: "driver_id", label: label("driver_id"), value: data?.driver || data?.assignedDriver || "" },
                      { key: "driver_name", label: label("driver_name"), value: data?.driver_name || data?.assignedDriverName || "" },
                      {
                        key: "driver_contact", label: label("driver_contact"), value: (data?.driver_contact || data?.assignedDriverContact) ? (settings.AllowCriticalEditsAdmin
                          ? (data?.driver_contact || data?.assignedDriverContact)
                          : label("hidden_demo")) : ""
                      },
                      {
                        key: "driver_email", label: label("driver_email"), value: data?.driver_email ? (settings.AllowCriticalEditsAdmin
                          ? data?.driver_email
                          : label("hidden_demo")) : ""
                      },
                      { key: "car_type", label: label("car_type"), value: data?.carType ? data.carType : "" },
                      { key: "assigned_vehicle", label: labelText(t, "assigned_vehicle", "Assigned Vehicle"), value: data?.assignedVehicleName || "" },
                      { key: "vehicle_no", label: label("vehicle_no"), value: data?.vehicle_number || data?.assignedVehiclePlate || "" },
                      { key: "assigned_by", label: labelText(t, "assigned_by", "Assigned By"), value: data?.assignedBy || "" },
                      { key: "assigned_at", label: labelText(t, "assigned_at", "Assigned At"), value: data?.assignedAt ? moment(data.assignedAt).format("lll") : "" },
                      { key: "fleetadmins", label: label("fleetadmins"), value: data?.fleetadmin && role === 'admin' ? data.fleetadmin : "" },
                      { key: "device_id", label: label("device_id"), value: data?.driverDeviceId ? data.driverDeviceId : "" },

                    ].map((item) =>
                      item.value ? renderGridItem(item, isRTL): null
                    )}
                </Grid>
              </Card>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={3}>
            <Grid item>
              <Card className={classes.card}>
                {renderTypography(label("customer_info"))}
                <Grid container direction="column" style={{ padding: 15 }}>
                  <Grid
                    item
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {data?.customer_image ? (
                      <Avatar
                        alt="customer profile image"
                        src={data?.customer_image}
                        style={{ width: 100, height: 100, objectFit: "cover" }}
                      />
                    ) : (
                      <AccountCircleIcon sx={{ width: 100, height: 100 }} />
                    )}
                  </Grid>
                  {data &&
                    [
                      { key: "customer_id", label: label("customer_id"), value: data?.customer ? data.customer : "" },
                      { key: "customer_name", label: label("customer_name"), value: data?.customer_name ? data.customer_name : "" },
                      {
                        key: "customer_contact", label: label("customer_contact"), value: data?.customer_contact ? (settings.AllowCriticalEditsAdmin
                          ? data?.customer_contact
                          : label("hidden_demo")) : ""
                      },
                      {
                        key: "customer_email", label: label("customer_email"), value: data?.customer_email ? (settings.AllowCriticalEditsAdmin
                          ? data?.customer_email
                          : label("hidden_demo")) : ""
                      },

                    ].map((item) =>
                      item.value ? renderGridItem(item, isRTL): null
                    )}
                </Grid>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}

export default BookingDetails;
